/**
 * <majer-quantity-input> — PDP quantity input se třemi režimy (REZ-263).
 *
 * Režim, krok a převody dodává MajerUnits (assets/majer-units.js); tento element jen
 * drží DOM: viditelné pomocné inputy + skryté pole name="quantity" (CartQuantity).
 * data-* atributy plní Liquid snippet majer-quantity-input.liquid z Variant Metafields.
 *
 * Po změně varianty product-info.js přerenderuje celý #Quantity-Form-{section}
 * ze section HTML → element se znovu připojí a inicializuje (connectedCallback).
 * Má-li nová varianta stejný vstup množství (MajerUnits.canKeepQuantity), převezme
 * zadané množství předchozí varianty místo návratu na minimum.
 *
 * Návrat Zpět (bfcache / obnova formuláře prohlížečem) může vrátit viditelné pole bez skrytého
 * CartQuantity → na `pageshow` se CartQuantity (a tím Celkem) znovu dopočítá z viditelné hodnoty.
 * Celkem (<majer-total>) je v assets/majer-total.js.
 *
 * Bulk karta (REZ-289, atribut data-cart-quantity): input ukazuje stav košíku pro variantu karty
 * (0 = není v košíku), +/− mění CartQuantity o jeden krok a zapsaná hodnota se při potvrzení převede
 * přes MajerUnits.cardCartQuantity. Nová CartQuantity jde do skrytého pole (change event), odkud ji
 * quick-add-bulk.js pošle do košíku.
 */

class MajerQuantityInput extends HTMLElement {
  /** Poslední CartQuantity a jednotkové info na formulář (klíč = id product formu), pro změnu varianty. */
  static lastQuantity = new Map();

  connectedCallback() {
    if (typeof MajerUnits === 'undefined') return;

    this.info = MajerUnits.resolveUnitInfo(
      {
        unit: this.dataset.unit,
        unitAmount: this.dataset.unitAmount,
        isDivisible: this.dataset.divisible,
        convertToPieces: this.dataset.convertToPieces,
      },
      {}
    );
    this.config = MajerUnits.getInputConfig(this.info);

    this.cartInput = this.querySelector('[data-role="cart-quantity"]');
    this.unitInput = this.querySelector('[data-role="unit"]');
    this.ksInput = this.querySelector('[data-role="ks"]');

    if (this.dataset.cartQuantity != null) {
      this.connectCard();
      return;
    }

    this.querySelectorAll('[data-qty-btn]').forEach((btn) => {
      btn.addEventListener('click', () => this.handleButton(btn.dataset.qtyBtn));
    });

    if (this.unitInput) {
      this.unitInput.addEventListener('input', () => this.syncFromUnit());
      this.unitInput.addEventListener('blur', () => this.roundUnit());
    }
    if (this.ksInput) {
      this.ksInput.addEventListener('input', () => this.syncFromKs());
      this.ksInput.addEventListener('blur', () => this.roundKs());
    }

    this.formKey = this.cartInput ? this.cartInput.getAttribute('form') : null;
    const previous = this.formKey ? MajerQuantityInput.lastQuantity.get(this.formKey) : null;
    if (previous && MajerUnits.canKeepQuantity(previous.info, this.info)) {
      this.restoreCartQuantity(previous.cartQuantity);
    } else {
      this.syncFromUnit();
    }
    this.remember();

    this.onPageShow = () => this.syncFromUnit();
    window.addEventListener('pageshow', this.onPageShow);
  }

  /** Bulk karta: stav košíku místo minima, změny po celých krocích (CartQuantity ± 1). */
  connectCard() {
    this.showCartQuantity(this.dataset.cartQuantity);

    this.querySelectorAll('[data-qty-btn]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const delta = btn.dataset.qtyBtn.startsWith('plus') ? 1 : -1;
        this.setCardQuantity(Math.max(0, this.currentCardQuantity() + delta));
      });
    });

    [this.unitInput, this.ksInput].forEach((input) => {
      if (!input) return;
      input.addEventListener('change', () => {
        const cartQuantity = MajerUnits.cardCartQuantity(this.info, input.dataset.role, input.value);
        if (cartQuantity === null) {
          this.showCartQuantity(this.currentCardQuantity());
          return;
        }
        this.setCardQuantity(cartQuantity);
      });
    });
  }

  currentCardQuantity() {
    return parseInt(this.cartInput.value, 10) || 0;
  }

  /** Zobrazí CartQuantity v jednotce inputu (u DualInput ks + celkové množství). */
  showCartQuantity(cartQuantity) {
    const values = MajerUnits.cardInputValues(this.info, cartQuantity);
    if (this.unitInput) this.unitInput.value = values.unit === 0 ? '0' : values.unit.toFixed(this.config.decimals);
    if (this.ksInput) this.ksInput.value = values.ks;
  }

  setCardQuantity(cartQuantity) {
    this.showCartQuantity(cartQuantity);
    const next = String(cartQuantity);
    if (this.cartInput.value === next) return;
    this.cartInput.value = next;
    this.cartInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  disconnectedCallback() {
    if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
    this.onPageShow = null;
  }

  /** Zobrazí uloženou CartQuantity (po změně varianty se stejným vstupem). */
  restoreCartQuantity(cartQuantity) {
    const value = MajerUnits.fromCartQuantity(this.info, cartQuantity);
    if (this.mode === 'dual') {
      this.ksInput.value = Math.max(1, value);
      this.syncFromKs();
    } else {
      this.setUnitValue(Math.max(this.config.min, value));
    }
  }

  remember() {
    if (!this.formKey || !this.cartInput) return;
    MajerQuantityInput.lastQuantity.set(this.formKey, { info: this.info, cartQuantity: this.cartInput.value });
  }

  get mode() {
    return this.config.mode;
  }

  /** Aktuální CartQuantity (celé číslo), odvozená ze zobrazené hodnoty. */
  get cartQuantity() {
    if (this.mode === 'dual') {
      return MajerUnits.toCartQuantity(this.info, this.ksInput.value);
    }
    return MajerUnits.toCartQuantity(this.info, this.unitInput.value);
  }

  handleButton(role) {
    switch (role) {
      case 'minus-ks':
        this.ksInput.value = Math.max(1, (parseInt(this.ksInput.value, 10) || 1) - 1);
        this.syncFromKs();
        break;
      case 'plus-ks':
        this.ksInput.value = (parseInt(this.ksInput.value, 10) || 1) + 1;
        this.syncFromKs();
        break;
      case 'minus-unit': {
        const current = MajerUnits.roundToStep(this.info, this.unitInput.value);
        this.setUnitValue(Math.max(this.config.min, current - this.config.step));
        break;
      }
      case 'plus-unit': {
        const current = MajerUnits.roundToStep(this.info, this.unitInput.value);
        this.setUnitValue(current + this.config.step);
        break;
      }
    }
  }

  setUnitValue(value) {
    this.unitInput.value = Number(value).toFixed(this.config.decimals);
    this.syncFromUnit();
  }

  /** Zobrazená hodnota → CartQuantity (+ u DualInput dopočet ks). Nechává uživatele psát volně. */
  syncFromUnit() {
    if (!this.unitInput) return;
    if (this.mode === 'dual') {
      const pieces = MajerUnits.amountToPieces(this.info, this.unitInput.value);
      this.ksInput.value = pieces;
    }
    this.updateCartQuantity();
  }

  /** DualInput: ks → celkové množství. */
  syncFromKs() {
    const pieces = Math.max(1, parseInt(this.ksInput.value, 10) || 1);
    this.unitInput.value = MajerUnits.piecesToAmount(this.info, pieces).toFixed(this.config.decimals);
    this.updateCartQuantity();
  }

  /** onBlur: zaokrouhlení na nejbližší násobek kroku (min. minimum). */
  roundUnit() {
    const rounded = MajerUnits.roundToStep(this.info, this.unitInput.value);
    this.setUnitValue(rounded);
  }

  roundKs() {
    this.ksInput.value = Math.max(1, parseInt(this.ksInput.value, 10) || 1);
    this.syncFromKs();
  }

  updateCartQuantity() {
    if (!this.cartInput) return;
    const next = String(Math.max(1, this.cartQuantity));
    if (this.cartInput.value === next) return;
    this.cartInput.value = next;
    this.remember();
    this.cartInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('majer-quantity-input')) {
  customElements.define('majer-quantity-input', MajerQuantityInput);
}
